import { MenuItemData } from '@components/Navigation';
import { useLocation } from '@reach/router';
import { mc, StyleProps } from '@styles';
import React, { FC, MouseEventHandler, useEffect, useState } from 'react';

import ChevronRightIcon from './assets/chevronRight.inline.svg';

interface Props extends StyleProps {
  item: MenuItemData;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  onClose?: () => void;
}

export const NavigationTreeViewDropdown: FC<Props> = ({ className, style, item, onMouseEnter, onMouseLeave, onClose }) => {
  const location = useLocation();
  const [activeLeftMenuItem, setActiveLeftMenuItem] = useState<MenuItemData | undefined>(undefined);

  useEffect(() => {
    if (!item.items) return;
    const curActiveLeftMenuItemByUrl = item.items.find(i => i.url && location.pathname.startsWith(i.url));
    setActiveLeftMenuItem(curActiveLeftMenuItemByUrl ? curActiveLeftMenuItemByUrl : item.items[0]);
  }, [location.pathname]);

  const isRightMenuItemActive = (itm: MenuItemData) => {
    if (!itm.url || location.pathname === '/') return false;
    // Ignore items with category URLs
    if (itm.title.toLocaleLowerCase().startsWith('all ')) return false;
    return location.pathname.startsWith(itm.url);
  };

  const handleItemClick = () => onClose && onClose();

  const renderLeftMenuItem = (leftMenuItem: MenuItemData) => {
    const subItemBtnClassName = mc(
      'px-5 py-2 w-full',
      'flex flex-row items-center justify-between',
      'text-left text-lg font-raleway leading-normal text-ocean-blue capitalize',
      'hover:bg-white hover:text-salmon',
      activeLeftMenuItem?.title === leftMenuItem.title && 'bg-white text-salmon',
    );
    const { title, url, items } = leftMenuItem;
    const showIcon = items && items.length > 0;
    return (
      <li key={title} className="mb-3 w-full">
        {url ? (
          <a
            className={subItemBtnClassName}
            href={url}
            onClick={handleItemClick}
            onMouseEnter={() => setActiveLeftMenuItem(leftMenuItem)}
          >
            <span className="block">{title}</span>
            {showIcon && <ChevronRightIcon className="block" />}
          </a>
        ) : (
          <button
            className={subItemBtnClassName}
            onClick={handleItemClick}
            onMouseEnter={() => setActiveLeftMenuItem(leftMenuItem)}
          >
            <span className="block">{title}</span>
            {showIcon && <ChevronRightIcon className="block" />}
          </button>
        )}
      </li>
    );
  };

  return (
    <div
      className={mc('flex flex-row justify-items-stretch', className)}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Left column */}
      <div className="py-10 pl-16 w-[360px] flex flex-col bg-light-gray">
        <div className="mb-2 pl-5 text-xl italic font-raleway leading-normal tracking-wide font-semibold text-ocean-blue">
          Discover
        </div>
        <ul className="w-full">{item.items?.map(renderLeftMenuItem)}</ul>
      </div>
      {/* Left column */}
      {/* Right column */}
      <div className="flex-1 px-14 py-10 bg-white">
        {item.items?.map(subItem =>
          !subItem.items?.length ? null : (
            <div key={subItem.title} className={mc(subItem.title === activeLeftMenuItem?.title ? undefined : 'hidden')}>
              {!!subItem.subtitle && (
                <div className="px-5 mb-3 text-xl font-italic font-sharpsans leading-normal tracking-wide font-semibold text-ocean-blue italic">
                  {subItem.subtitle}
                </div>
              )}
              <ul>
                {subItem.items?.map(itm => (
                  <li key={itm.title} className="mb-3">
                    <a
                      className={mc(
                        'px-5 py-2',
                        'text-lg font-raleway leading-normal',
                        'text-ocean-blue',
                        'hover:text-salmon',
                        isRightMenuItemActive(itm) && 'text-salmon',
                      )}
                      href={itm.url || '#'}
                      onClick={handleItemClick}
                    >
                      {itm.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ),
        )}
      </div>
      {/* Right column */}
    </div>
  );
};

export type NavigationTreeViewDropdownProps = Props;
export default NavigationTreeViewDropdown;
